// T-170 Fase 1d — notifica locale immediata quando un job import è 'ready'.
// Dedupe per jobId (localStorage), guard permesso, web fallback. Id notifica
// namespaced dal jobId, fuori dal range di expirationNotifier (offset costante).
// Best-effort: la sezione "In elaborazione" resta la fonte di verità.

import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

const NOTIFIED_KEY = 'trackeat.jobReadyNotified';
// Range id dedicato (expirationNotifier usa hash% 2e9 +1; qui partiamo da 2.0e9+).
const JOB_NOTIF_BASE = 2_000_000_100;

function notifiedSet(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(NOTIFIED_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}
function persistNotified(s: Set<string>): void {
  try {
    localStorage.setItem(NOTIFIED_KEY, JSON.stringify([...s].slice(-200)));
  } catch {
    // ignore
  }
}
function notifIdFor(jobId: string): number {
  let h = 0;
  for (let i = 0; i < jobId.length; i++) h = (h * 31 + jobId.charCodeAt(i)) | 0;
  return JOB_NOTIF_BASE + (Math.abs(h) % 1_000_000);
}

async function ensurePermission(): Promise<boolean> {
  const cur = await LocalNotifications.checkPermissions();
  if (cur.display === 'granted') return true;
  const req = await LocalNotifications.requestPermissions();
  return req.display === 'granted';
}

/**
 * Notifica che un documento è pronto da rivedere. Idempotente per jobId.
 * `foreground=true` → non notifica (l'utente è già nell'app sulla lista).
 */
export async function fireJobReady(args: {
  jobId: string;
  supplierName?: string | null;
  foreground?: boolean;
}): Promise<void> {
  const { jobId, supplierName, foreground } = args;
  const seen = notifiedSet();
  if (seen.has(jobId)) return;             // già notificato (background) → skip
  // Foreground: NON notifichiamo e NON marchiamo come "seen" — altrimenti se lo
  // stesso job diventasse ready in background (il caso d'uso reale della push)
  // la dedupe lo silenzierebbe per sempre. La lista in foreground dedupe da sé.
  if (foreground) return;
  seen.add(jobId);
  persistNotified(seen);

  const who = supplierName && supplierName.trim() ? supplierName.trim() : 'un fornitore';
  const title = 'Documento pronto da rivedere';
  const body = `Il documento di ${who} è stato analizzato. Tocca per rivederlo.`;

  if (Capacitor.isNativePlatform()) {
    try {
      if (!(await ensurePermission())) return;
      await LocalNotifications.schedule({
        notifications: [
          {
            id: notifIdFor(jobId),
            title,
            body,
            // niente `schedule` → fire immediato.
            extra: { jobId, kind: 'document_job_ready' },
          },
        ],
      });
    } catch {
      // permesso negato / limite OS → best-effort, la lista resta la verità.
    }
    return;
  }

  // Web fallback.
  if (typeof Notification === 'undefined') return;
  let permission = Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch {
      return;
    }
  }
  if (permission !== 'granted') return;
  try {
    new Notification(title, { body, tag: `job-${jobId}` });
  } catch {
    // ignore
  }
}
